import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import {
	faClipboardList,
	faCommentAltLines,
	faEdit,
	faEllipsisV,
	faExpandAlt,
	faFileMinus,
	faLink,
	faPaperclip,
	faPrint,
	faSparkles,
	faUserSlash,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import { AGENDA_BUILDER_FEATURE_FLAG } from "~/agendaBuilder";
import growthbook from "~/lib/ab/growthbook";
import { inCalendarMode, inCompanionMode, isMobile, isNative, isTeams, isTouchScreen, isZoom } from "~/lib/utils";
import { useShouldShowMeetingSummaryBanner } from "~/meetingRecorder/MeetingSummaryBanner";
import {
	completeClientSideMeetingRecording,
	createClientSideRecording,
	getSignedUploadForClientSideMeetingRecording,
	MicrophoneRecordingSubmenu,
	useMediaRecorder,
} from "~/meetingRecorder/RecordingControls/MicrophoneRecording";
import useIsMicrophoneRecordingEnabled from "~/meetingRecorder/RecordingControls/MicrophoneRecording";
import { copyToClipboard } from "~/meetingRecorder/utils";
import ApplyTemplateMenuItem from "~/meetings/MeetingHeader/MoreActionsMenu/ApplyTemplate";
import {
	CarryForwardActionItems,
	CarryForwardTalkingPoints,
} from "~/meetings/MeetingHeader/MoreActionsMenu/CarryForward";
import DeleteMeetingMenuItem from "~/meetings/MeetingHeader/MoreActionsMenu/DeleteMeeting";
import EditTemplateMenuItem from "~/meetings/MeetingHeader/MoreActionsMenu/EditTemplate";
import LockNoteMenuItem from "~/meetings/MeetingHeader/MoreActionsMenu/LockNote";
import { MenuItemTooltip } from "~/meetings/MeetingHeader/MoreActionsMenu/MenuItemTooltip";
import RecapLanguageMenuItem from "~/meetings/MeetingHeader/MoreActionsMenu/RecapLanguage";
import SaveAsTemplateMenuItem from "~/meetings/MeetingHeader/MoreActionsMenu/SaveAsTemplate";
import VersionHistory from "~/meetings/MeetingHeader/MoreActionsMenu/VersionHistory";
import { uploadFileToURL } from "~/recordings/uploads/UploadModal";
import { type DeletionType } from "~/stream/DeleteNoteModal/DeleteNoteModal";
import { useModals } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import RenameNoteModal from "../../RenameNoteModal";

import type { MoreActionsMenu_note$key } from "~/__generated__/MoreActionsMenu_note.graphql";
import type { MoreActionsMenuNote_viewer$key } from "~/__generated__/MoreActionsMenuNote_viewer.graphql";

export interface MoreActionsMenuProps {
	embedded?: boolean;
	inAgendaBuilderMode: boolean;
	onFullscreenClick?: () => void;
	onAttachFilesClick?: () => void;
	onNotOneononeClick?: () => void;
	onUnlinkClick?: () => void;
	onAgendaGeneratorClick?: () => void;
	onGeneratedHeadingFlowClick?: () => void;
	onResolvedCommentsClick?: () => void;
	printProps?: {
		inPrintNodeMode: boolean;
		isIsolatedNoteCard: boolean;
		isolateThisNote: () => void;
	};
	allowCarryForward?: boolean;
	recordingId?: string;
	onDeleteExecuted: (type: DeletionType) => void;
}

export default function MoreActionsMenu({
	onFullscreenClick,
	onAttachFilesClick,
	onNotOneononeClick,
	onUnlinkClick,
	onResolvedCommentsClick,
	onAgendaGeneratorClick,
	onGeneratedHeadingFlowClick,
	note: noteKey,
	viewer: viewerKey,
	embedded,
	printProps,
	inAgendaBuilderMode,
	allowCarryForward = true,
	recordingId,
	onDeleteExecuted,
	inMeetingModal,
}: MoreActionsMenuProps & {
	note: MoreActionsMenu_note$key;
	viewer: MoreActionsMenuNote_viewer$key;
	inMeetingModal?: boolean;
}) {
	const note = useFragment(
		graphql`
			fragment MoreActionsMenu_note on Note {
				id
				noteTitle: title
				isLocked
				type
				viewerCanEdit
				meetingRecordingLink {
					url
				}
				stream {
					id
					isExternal
					displayName
					account {
						name
					}
				}
				previousNote {
					pendingAgendaCount
					pendingTodoCount
				}
				hasMeetingRecordings
				meetingRecordings {
					id
					viewerCanDelete
					...RecapHistory_meetingRecording
					...RegenerateForLanguageModalFragment_meetingRecording
				}
				...MeetingSummaryBanner_useShouldShowMeetingSummaryBanner_note
				...ApplyTemplate_note
				...EditTemplateMenuItem_note
				...SaveAsTemplate_note
				...DeleteNoteModal_note
				...RecapLanguage_note
				...CarryForward_MoreActionsMenu_note
				...VersionHistory_note
				...LockNote_note
				...DeleteMeeting_note
			}
		`,
		noteKey,
	);

	const viewer = useFragment(
		graphql`
			fragment MoreActionsMenuNote_viewer on Viewer {
				user {
					id
					isGuest
				}
				account {
					allowAiFeatures
				}
				...SaveAsTemplate_viewer
				...VersionHistory_viewer
				...LockNote_viewer
				...DeleteMeeting_viewer
			}
		`,
		viewerKey,
	);

	const modals = useModals();

	const isExternal = note.stream.isExternal;
	const { isLocked } = note;
	const hasAccessToAgendaGenerator = growthbook.isOn("ai-generate-agenda") && viewer.account.allowAiFeatures;

	const openRenameModal = React.useCallback(
		() => modals.open(({ close }) => <RenameNoteModal id={note.id} initialTitle={note.noteTitle} onClose={close} />),
		[modals, note.id, note.noteTitle],
	);

	const onPrint = React.useCallback(() => {
		printProps?.isolateThisNote();
		setTimeout(window.print);
	}, [printProps]);

	const tooltipPropsForExternalWorkspaces = {
		externalWorkspaceName: isExternal ? note.stream.account.name : undefined,
	};

	const shouldShowMeetingSummaryBanner = useShouldShowMeetingSummaryBanner(note);

	const noteHasTitle = React.useMemo(
		() => Boolean(note.noteTitle && note.stream.displayName !== note.noteTitle),
		[note],
	);

	const { isGuest } = viewer.user;

	const onCopyLink = React.useCallback(() => {
		copyToClipboard(note.meetingRecordingLink.url);
	}, [note.meetingRecordingLink.url]);

	const isMicrophoneRecordingEnabled = useIsMicrophoneRecordingEnabled();

	// Microphone recording state
	const {
		startRecording: startMicRecording,
		pauseRecording,
		resumeRecording,
		stopRecording: stopMicRecording,
		state: micRecordingState,
	} = useMediaRecorder();

	const handleStartRecording = useCallback(async () => {
		try {
			await startMicRecording();
		} catch (error) {
			toast.error("Failed to start recording");
			throw error;
		}
	}, [startMicRecording]);

	const handleStopRecording = useCallback(async () => {
		try {
			const [file, pauses] = await stopMicRecording();
			const meetingRecording = await createClientSideRecording(note.id);
			const [uploadUrl, uploadFields] = await getSignedUploadForClientSideMeetingRecording(
				meetingRecording.id,
				file.name.split(".").pop() ?? "m4a",
			);
			await uploadFileToURL(file, uploadUrl, uploadFields, progress => console.log(`Uploading… ${progress}%`));
			await completeClientSideMeetingRecording(meetingRecording.id, uploadFields.key, pauses);
		} catch (error) {
			toast.error("Failed to stop recording");
			throw error;
		}
	}, [stopMicRecording, note.id]);

	const handlePauseRecording = useCallback(() => {
		pauseRecording();
	}, [pauseRecording]);

	const handleResumeRecording = useCallback(() => {
		resumeRecording();
	}, [resumeRecording]);

	if (isZoom || inCompanionMode || (embedded && !isTeams)) return null;

	const shouldShowEditAndSaveTemplateMenuItem = !inCalendarMode && !isTouchScreen;

	return (
		<Menu mobileHeaderTitle="More Actions">
			<Menu.Trigger>
				<IconButton title="More actions" icon={faEllipsisV} data-test-id="note-header-dropdown" />
			</Menu.Trigger>
			<Menu.Content side="bottom" sideOffset={5} align="end">
				{!note.viewerCanEdit ? (
					<Menu.Item icon={faLink} onSelect={onCopyLink}>
						Copy link
					</Menu.Item>
				) : (
					<>
						{!isGuest && (
							<>
								{hasAccessToAgendaGenerator && (
									<>
										<Menu.Item icon={faSparkles} onSelect={onAgendaGeneratorClick} disabled={isLocked || isExternal}>
											<MenuItemTooltip
												disabled={!isLocked && !isExternal}
												content="Cannot generate an agenda to locked notes."
												data-test-id="note-header-dropdown-generateagenda-item"
											>
												<span>Generate an agenda</span>
											</MenuItemTooltip>
										</Menu.Item>
										<Menu.Separator />
									</>
								)}
								{!shouldShowMeetingSummaryBanner && onGeneratedHeadingFlowClick && !hasAccessToAgendaGenerator && (
									<>
										<Menu.Item
											icon={faSparkles}
											onSelect={onGeneratedHeadingFlowClick}
											disabled={isLocked || isExternal}
											data-test-id={`note-header-dropdown-${AGENDA_BUILDER_FEATURE_FLAG}-item`}
										>
											<MenuItemTooltip
												disabled={!(isLocked || isExternal)}
												content="Feature disabled in locked notes."
												{...tooltipPropsForExternalWorkspaces}
											>
												<span>Use AI-generated agenda</span>
											</MenuItemTooltip>
										</Menu.Item>
										<Menu.Separator />
									</>
								)}
								{!isTeams &&
									!inMeetingModal &&
									(shouldShowEditAndSaveTemplateMenuItem ? (
										<Menu.Sub mobileHeaderTitle="Templates">
											<Menu.SubTrigger icon={faClipboardList}>Template…</Menu.SubTrigger>
											<Menu.SubContent>
												<ApplyTemplateMenuItem note={note} />
												<EditTemplateMenuItem note={note} />
												<SaveAsTemplateMenuItem viewer={viewer} note={note} />
											</Menu.SubContent>
										</Menu.Sub>
									) : (
										<ApplyTemplateMenuItem note={note} />
									))}

								{allowCarryForward && (
									<>
										<Menu.Separator />
										<CarryForwardTalkingPoints note={note} />
										<CarryForwardActionItems note={note} />
										<Menu.Separator />
									</>
								)}

								<Menu.Item icon={faEdit} onSelect={openRenameModal} disabled={isLocked || isExternal}>
									<MenuItemTooltip
										disabled={!(isLocked || isExternal)}
										content="Locked notes cannot be renamed."
										data-test-id="note-header-dropdown-renamenote-item"
										{...tooltipPropsForExternalWorkspaces}
									>
										{noteHasTitle ? <span>Edit note title…</span> : <span>Add note title…</span>}
									</MenuItemTooltip>
								</Menu.Item>

								<LockNoteMenuItem note={note} viewer={viewer} inAgendaBuilderMode={inAgendaBuilderMode} />
							</>
						)}
						{!isMobile && printProps && (
							<Menu.Item icon={faPrint} onSelect={onPrint}>
								Print…
							</Menu.Item>
						)}
						{onAttachFilesClick && (
							<Menu.Item icon={faPaperclip} onSelect={onAttachFilesClick} disabled={isLocked}>
								Attach files…
							</Menu.Item>
						)}

						{onResolvedCommentsClick && <Menu.Separator />}
						{!isNative && !isMobile && <VersionHistory note={note} viewer={viewer} selectedRecordingId={recordingId} />}
						{viewer.account.allowAiFeatures && !isGuest && recordingId && (
							<RecapLanguageMenuItem note={note} selectedRecordingId={recordingId} />
						)}
						{onResolvedCommentsClick && (
							<Menu.Item icon={faCommentAltLines} onSelect={onResolvedCommentsClick}>
								Resolved comments
							</Menu.Item>
						)}

						{(onFullscreenClick || onNotOneononeClick || note.viewerCanEdit || onUnlinkClick) && <Menu.Separator />}
						{onFullscreenClick && (
							<Menu.Item icon={faExpandAlt} onSelect={onFullscreenClick}>
								Fullscreen
							</Menu.Item>
						)}
						{!isGuest && onNotOneononeClick && (
							<Menu.Item
								icon={faUserSlash}
								onSelect={onNotOneononeClick}
								data-test-id="note-header-dropdown-not1on1-item"
							>
								Not a 1-on-1…
							</Menu.Item>
						)}
						{!isGuest && onUnlinkClick && (
							<Menu.Item icon={faFileMinus} onSelect={onUnlinkClick}>
								Unlink note…
							</Menu.Item>
						)}
						<DeleteMeetingMenuItem
							note={note}
							viewer={viewer}
							selectedRecordingId={recordingId}
							onDeleteExecuted={onDeleteExecuted}
						/>
						{isMicrophoneRecordingEnabled && (
							<MicrophoneRecordingSubmenu
								noteId={note.id}
								state={micRecordingState}
								startRecording={handleStartRecording}
								pauseRecording={handlePauseRecording}
								resumeRecording={handleResumeRecording}
								stopRecording={handleStopRecording}
							/>
						)}
					</>
				)}
			</Menu.Content>
		</Menu>
	);
}
